import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getTimeAgo } from "../../../utils/helpers";

const EnhancedChatList = ({ chats = [], searchQuery = "" }) => {
  const navigate = useNavigate();

  const groupedChats = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    const groups = {
      today: [],
      yesterday: [],
      thisWeek: [],
      older: []
    };

    chats.forEach(chat => {
      const updatedAt = chat.updated_at ? new Date(chat.updated_at) : new Date(chat.timestamp);
      
      if (updatedAt >= today) {
        groups.today.push(chat);
      } else if (updatedAt >= yesterday) {
        groups.yesterday.push(chat);
      } else if (updatedAt >= weekAgo) {
        groups.thisWeek.push(chat);
      } else {
        groups.older.push(chat);
      }
    });

    // Sort each group by updated_at descending
    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) => {
        const dateA = a.updated_at ? new Date(a.updated_at) : new Date(a.timestamp);
        const dateB = b.updated_at ? new Date(b.updated_at) : new Date(b.timestamp);
        return dateB - dateA;
      });
    });

    return groups;
  }, [chats]);

  const handleChatClick = (chat) => {
    if (chat.space) {
      navigate(`/spectra/${chat.space}/chat/${chat.uid}`);
    } else {
      navigate(`/chat/${chat.uid}`);
    }
  };

  const renderChatCard = (chat) => (
    <div
      key={chat.uid}
      onClick={() => handleChatClick(chat)}
      className="bg-fill-white dark:bg-fill-dialog-surface rounded-xl border border-edge-gray-200 dark:border-edge-panel-border p-4 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-edge-primary dark:hover:border-edge-accent group"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {chat.pinned && (
            <svg className="w-4 h-4 text-ink-amber-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L11 6.477V16h2a1 1 0 110 2H7a1 1 0 110-2h2V6.477L6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" />
            </svg>
          )}
          <h4 className="font-semibold text-ink-ink dark:text-ink-white truncate group-hover:text-ink-primary dark:group-hover:text-ink-accent transition-colors">
            {chat.title || "Untitled Chat"}
          </h4>
        </div>
        <span className="text-xs text-ink-gray-500 dark:text-ink-gray-400 whitespace-nowrap">
          {getTimeAgo(chat.updated_at || chat.timestamp)}
        </span>
      </div>

      {/* Spectra Association */}
      {chat.space && (
        <div className="flex items-center gap-1.5 mb-2">
          <svg className="w-3.5 h-3.5 text-ink-blue-500" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
          </svg>
          <span className="text-xs text-ink-blue-500 font-medium">
            Spectra: {chat.space_name || "Workspace"}
          </span>
        </div>
      )}

      {!chat.space && (
        <div className="flex items-center gap-1.5 mb-2">
          <svg className="w-3.5 h-3.5 text-ink-gray-500 dark:text-ink-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
          <span className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
            Private Chat
          </span>
        </div>
      )}

      {/* Last Message Preview */}
      {chat.last_message && (
        <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400 mb-3 line-clamp-2">
          "{chat.last_message}"
        </p>
      )}

      {/* Footer */}
      <div className="flex items-center gap-3 text-xs text-ink-gray-500 dark:text-ink-gray-400">
        <div className="flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
          </svg>
          <span>{chat.message_count || 0} messages</span>
        </div>
        {chat.collaborators && chat.collaborators.length > 0 && (
          <div className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
            </svg>
            <span>
              {chat.collaborators.length === 1 ? 'You' : `You, ${chat.collaborators.slice(0, 2).map(c => c.name || c.email?.split('@')[0]).join(', ')}`}
              {chat.collaborators.length > 3 && ` +${chat.collaborators.length - 3}`}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  const renderGroup = (title, chats) => {
    if (chats.length === 0) return null;

    return (
      <div key={title} className="mb-8">
        <h3 className="text-xs font-semibold text-ink-gray-500 dark:text-ink-gray-400 uppercase tracking-wider mb-3">
          {title}
        </h3>
        <div className="space-y-3">
          {chats.map(renderChatCard)}
        </div>
      </div>
    );
  };

  if (chats.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-16 h-16 rounded-full bg-fill-gray-100 dark:bg-fill-gray-700 flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-ink-gray-500 dark:text-ink-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-ink-ink dark:text-ink-white mb-2">
          {searchQuery ? "No chats found" : "No chats yet"}
        </h3>
        <p className="text-ink-gray-500 dark:text-ink-gray-400 max-w-sm">
          {searchQuery 
            ? `No chats match "${searchQuery}". Try a different search term.`
            : "Start a conversation by clicking the 'New Chat' button above."
          }
        </p>
      </div>
    );
  }

  return (
    <div>
      {renderGroup("Today", groupedChats.today)}
      {renderGroup("Yesterday", groupedChats.yesterday)}
      {renderGroup("This Week", groupedChats.thisWeek)}
      {renderGroup("Older", groupedChats.older)}
    </div>
  );
};

export default EnhancedChatList;
